import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import Checkbox, { CheckboxChangeEvent } from "antd/lib/checkbox";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

interface InlineLoadingCheckboxProps {
  name: string;
  label: string;
  loading: boolean;
  checked: boolean;
  disabled?: boolean;
  onChange?: (e: CheckboxChangeEvent) => void;
}
export default function InlineLoadingCheckbox({
  name,
  label,
  loading,
  checked,
  disabled,
  onChange,
}: InlineLoadingCheckboxProps): JSX.Element {
  const wrapperRef = useRef<HTMLLabelElement | null>(null);
  const [spinnerPos, setSpinnerPos] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const SPINNER_SIZE = 14;

  const computePosition = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      setSpinnerPos(null);
      return;
    }

    const checkboxEl = wrapper.querySelector(
      ".ant-checkbox"
    ) as HTMLElement | null;
    if (!checkboxEl) {
      setSpinnerPos(null);
      return;
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    const cbRect = checkboxEl.getBoundingClientRect();

    const left =
      cbRect.left - wrapperRect.left + (cbRect.width - SPINNER_SIZE) / 2;
    const top =
      cbRect.top - wrapperRect.top + (cbRect.height - SPINNER_SIZE) / 2;

    setSpinnerPos({ left: Math.round(left), top: Math.round(top) });
  }, []);

  useLayoutEffect(() => {
    computePosition();
  }, [computePosition, loading, checked]);

  useEffect(() => {
    window.addEventListener("resize", computePosition);
    return () => window.removeEventListener("resize", computePosition);
  }, [computePosition]);

  return (
    <label
      ref={wrapperRef}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        userSelect: "none",
        cursor: loading ? "not-allowed" : "pointer",
      }}
      data-testid={`inline-loading-checkbox-${name}`}
    >
      <Checkbox
        name={name}
        checked={checked}
        disabled={disabled || loading}
        onChange={onChange}
      />
      <span>{label}</span>

      {loading && spinnerPos && (
        <div
          style={{
            position: "absolute",
            left: spinnerPos.left,
            top: spinnerPos.top,
            width: SPINNER_SIZE,
            height: SPINNER_SIZE,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          <Spin
            indicator={
              <LoadingOutlined style={{ fontSize: SPINNER_SIZE - 2 }} spin />
            }
          />
        </div>
      )}
    </label>
  );
}
